import api from './api';

export interface UserSearchResult {
  id: string;
  displayName: string;
  email: string;
  jobTitle?: string;
  department?: string;
}

export interface GroupDetail {
  id: string;
  displayName: string;
}

export interface UserPermissionPreview {
  user_id: string;
  email: string;
  displayName: string;
  jobTitle?: string;
  department?: string;
  groups: string[];
  group_details: GroupDetail[];
  accessible_routes: string[];
  is_global_admin: boolean;
}

export interface ImpersonationStatus {
  is_impersonating: boolean;
  admin_email?: string;
  impersonated_user_id?: string;
  impersonated_email?: string;
  started_at?: string;
}

export interface ImpersonationResponse {
  session_token: string;
  impersonated_user_id: string;
  impersonated_email: string;
  message: string;
}

class ImpersonationService {
  /**
   * Search for users in Azure AD
   */
  async searchUsers(
    query: string,
    limit: number = 20,
    employeesOnly: boolean = false
  ): Promise<UserSearchResult[]> {
    try {
      const response = await api.get('/impersonate/search', {
        params: { query, limit, employees_only: employeesOnly }
      });
      return response.data.users;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Preview user's permissions before impersonating
   */
  async previewUserPermissions(userId: string): Promise<UserPermissionPreview> {
    try {
      const response = await api.get(`/impersonate/preview/${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Start impersonating a user
   */
  async startImpersonation(userId: string): Promise<ImpersonationResponse> {
    try {
      const response = await api.post('/impersonate/start', {
        user_id: userId
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Stop impersonating
   */
  async stopImpersonation(): Promise<void> {
    try {
      await api.post('/impersonate/stop');
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get current impersonation status
   */
  async getStatus(): Promise<ImpersonationStatus> {
    try {
      const response = await api.get('/impersonate/status');
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export default new ImpersonationService();
